import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getBooksLedger, getBooksProfitLoss, getBooksBudgets, postBooksBudget,
  getExpenseCategories, getTerms,
} from '@/api/owner';
import {
  BookOpen, TrendingUp, TrendingDown, Plus, X, AlertCircle, Loader2,
  RefreshCw, DollarSign, Calendar, FileText, BarChart3,
} from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;
const money = (n: any) => {
  if (n == null || n === '') return '—';
  return '₦' + Number(n).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

type Tab = 'ledger' | 'pl' | 'budgets';

export function BooksPage() {
  const [tab, setTab] = useState<Tab>('ledger');
  const [error, setError] = useState<string | null>(null);

  // Ledger filters
  const [accountCode, setAccountCode] = useState('');
  const [ledgerFrom, setLedgerFrom] = useState('');
  const [ledgerTo, setLedgerTo] = useState('');

  // P&L filters
  const [plFrom, setPlFrom] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [plTo, setPlTo] = useState(() => new Date().toISOString().slice(0, 10));

  // Budget
  const [showNewBudget, setShowNewBudget] = useState(false);

  const ledgerQ = useQuery({
    queryKey: ['books-ledger', accountCode, ledgerFrom, ledgerTo],
    queryFn: () => getBooksLedger({
      account: accountCode || undefined,
      from: ledgerFrom || undefined,
      to: ledgerTo || undefined,
      per_page: 100,
    }).then((r: any) => r.data ?? r),
    enabled: tab === 'ledger',
  });
  const plQ = useQuery({
    queryKey: ['books-pl', plFrom, plTo],
    queryFn: () => getBooksProfitLoss({ from: plFrom, to: plTo }).then(unwrap),
    enabled: tab === 'pl',
  });
  const budgetsQ = useQuery({
    queryKey: ['books-budgets'],
    queryFn: () => getBooksBudgets().then(unwrap),
    enabled: tab === 'budgets' || showNewBudget,
  });
  // Moved here from inside conditional render — hooks must be top-level, always called
  const budgetCategoriesQ = useQuery({
    queryKey: ['owner-expense-categories'],
    queryFn: () => getExpenseCategories().then(unwrap),
    enabled: showNewBudget,
  });
  const budgetTermsQ = useQuery({
    queryKey: ['owner-terms'],
    queryFn: () => getTerms().then(unwrap),
    enabled: showNewBudget,
  });

  const ledgerPayload = ledgerQ.data;
  const ledgerRows = arr<any>(ledgerPayload?.data ?? ledgerPayload);
  const ledgerTotals = ledgerPayload?.totals ?? { debit: 0, credit: 0 };
  const pl = plQ.data;
  const budgets = arr<any>(budgetsQ.data);

  const refresh = () => { ledgerQ.refetch(); plQ.refetch(); budgetsQ.refetch(); };

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Finance</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Books</h1>
          <p className="text-slate-500 text-sm mt-1">
            General ledger · profit &amp; loss · budgets
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={refresh}
            className="px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          {tab === 'budgets' && (
            <button onClick={() => setShowNewBudget(true)}
              className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1">
              <Plus className="w-4 h-4" /> New budget
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-1 overflow-x-auto">
          {([
            { key: 'ledger' as Tab, label: 'Ledger', icon: BookOpen },
            { key: 'pl' as Tab, label: 'Profit & loss', icon: BarChart3 },
            { key: 'budgets' as Tab, label: 'Budgets', icon: Calendar },
          ]).map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 inline-flex items-center gap-2 transition ${
                  tab === t.key ? 'border-[#0f172a] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── LEDGER TAB ── */}
      {tab === 'ledger' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Account code</label>
              <input value={accountCode} onChange={(e) => setAccountCode(e.target.value)}
                placeholder="e.g. 1000" className="w-32 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">From</label>
              <input type="date" value={ledgerFrom} onChange={(e) => setLedgerFrom(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">To</label>
              <input type="date" value={ledgerTo} onChange={(e) => setLedgerTo(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            {(accountCode || ledgerFrom || ledgerTo) && (
              <button onClick={() => { setAccountCode(''); setLedgerFrom(''); setLedgerTo(''); }}
                className="text-xs font-semibold text-slate-500 hover:text-slate-800 px-2 py-2">
                Clear
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <p className="text-xs font-semibold text-slate-500">Total debit</p>
              <p className="text-2xl font-extrabold font-mono text-red-700 mt-1">{money(ledgerTotals.debit)}</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <p className="text-xs font-semibold text-slate-500">Total credit</p>
              <p className="text-2xl font-extrabold font-mono text-green-700 mt-1">{money(ledgerTotals.credit)}</p>
            </div>
          </div>

          {ledgerQ.isLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : ledgerRows.length === 0 ? (
            <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
              <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="font-semibold text-slate-700">No ledger entries</p>
              <p className="text-sm text-slate-500 mt-1">
                Ledger entries are created automatically when payments, expenses, payroll, and withdrawals are posted.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left">Date</th>
                    <th className="px-3 py-3 text-left">Account</th>
                    <th className="px-3 py-3 text-left">Description</th>
                    <th className="px-3 py-3 text-right">Debit</th>
                    <th className="px-3 py-3 text-right">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ledgerRows.map((r: any) => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">{r.account ?? '—'}</td>
                      <td className="px-3 py-2 text-slate-900">{r.description ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-red-700">
                        {Number(r.debit) > 0 ? money(r.debit) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-green-700">
                        {Number(r.credit) > 0 ? money(r.credit) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── P&L TAB ── */}
      {tab === 'pl' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">From</label>
              <input type="date" value={plFrom} onChange={(e) => setPlFrom(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">To</label>
              <input type="date" value={plTo} onChange={(e) => setPlTo(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          {plQ.isLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : !pl ? (
            <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
              <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="font-semibold text-slate-700">No P&L data</p>
              <p className="text-sm text-slate-500 mt-1">Pick a date range above.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <PLCard label="Fees billed" value={pl.fees_billed} tone="blue" icon={<TrendingUp className="w-4 h-4" />} />
                <PLCard label="Fees collected" value={pl.fees_collected} tone="green" icon={<DollarSign className="w-4 h-4" />} />
                <PLCard label="Expenses" value={pl.expenses} tone="red" icon={<TrendingDown className="w-4 h-4" />} />
                <PLCard label="Net" value={pl.net} tone={Number(pl.net) >= 0 ? 'green' : 'red'} icon={<BarChart3 className="w-4 h-4" />} />
              </div>

              {arr<any>(pl.expenses_by_category).length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="px-5 py-4 border-b border-slate-100">
                    <p className="font-bold text-slate-900">Expenses by category</p>
                    <p className="text-xs text-slate-500">
                      {fmtDate(pl.from)} → {fmtDate(pl.to)}
                    </p>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {arr<any>(pl.expenses_by_category).map((c: any, i: number) => (
                      <li key={i} className="px-5 py-3 flex justify-between items-center">
                        <span className="text-slate-900 font-medium">{c.category}</span>
                        <span className="font-mono font-semibold">{money(c.total)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-xs text-slate-500">
                This is a live P&L calculated from the general ledger. Income = fees billed for the period.
                Expenses = approved/paid expense entries.
              </p>
            </>
          )}
        </div>
      )}

      {/* ── BUDGETS TAB ── */}
      {tab === 'budgets' && (
        <>
          {budgetsQ.isLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : budgets.length === 0 ? (
            <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="font-semibold text-slate-700">No budgets set</p>
              <p className="text-sm text-slate-500 mt-1">
                Set a spending ceiling per category per term. See how much is left as expenses come in.
              </p>
              <button onClick={() => setShowNewBudget(true)}
                className="mt-4 px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1">
                <Plus className="w-4 h-4" /> Set first budget
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left">Category</th>
                    <th className="px-3 py-3 text-right">Budget</th>
                    <th className="px-3 py-3 text-right">Spent</th>
                    <th className="px-3 py-3 text-right">Remaining</th>
                    <th className="px-3 py-3 text-left">Progress</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {budgets.map((b: any) => {
                    const amt = Number(b.amount ?? 0);
                    const spent = Number(b.spent ?? 0);
                    const remaining = Number(b.remaining ?? (amt - spent));
                    const pct = amt > 0 ? Math.min(100, Math.round((spent / amt) * 100)) : 0;
                    const over = spent > amt;
                    return (
                      <tr key={b.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-900">
                          {b.category_name ?? b.category?.name ?? `Category #${b.expense_category_id}`}
                          {b.notes && <p className="text-xs text-slate-500 mt-0.5">{b.notes}</p>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{money(amt)}</td>
                        <td className="px-3 py-2 text-right font-mono">{money(spent)}</td>
                        <td className={`px-3 py-2 text-right font-mono font-bold ${over ? 'text-red-700' : 'text-green-700'}`}>
                          {money(remaining)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="w-32 bg-slate-200 rounded-full h-2">
                            <div className={`h-2 rounded-full ${over ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                              style={{ width: `${pct}%` }} />
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{pct}% used</p>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showNewBudget && (
        <NewBudgetModal
          categories={arr<any>(budgetCategoriesQ.data)}
          terms={arr<any>(budgetTermsQ.data)}
          onClose={() => setShowNewBudget(false)}
          onSuccess={() => {
            setShowNewBudget(false);
            budgetsQ.refetch();
            alert('Budget saved.');
          }}
          onError={(m) => setError(m)}
        />
      )}
    </div>
  );
}

function PLCard({ label, value, tone, icon }: {
  label: string; value: any; tone: 'blue' | 'green' | 'red'; icon: React.ReactNode;
}) {
  const tones = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
  };
  const valColor = tone === 'green' ? 'text-green-700' : tone === 'red' ? 'text-red-700' : 'text-slate-900';
  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg mb-2 ${tones[tone]}`}>
        {icon}
      </div>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <p className={`text-2xl font-extrabold font-mono mt-1 ${valColor}`}>{money(value)}</p>
    </div>
  );
}

function NewBudgetModal({ categories, terms, onClose, onSuccess, onError }: {
  categories: any[]; terms: any[]; onClose: () => void; onSuccess: () => void; onError: (msg: string) => void;
}) {
  const [termId, setTermId] = useState<number | ''>(terms[0]?.id ?? '');
  const [categoryId, setCategoryId] = useState<number | ''>(categories[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!termId) return onError('Pick a term');
    if (!categoryId) return onError('Pick a category');
    const amt = Number(amount);
    if (!amt || amt < 0) return onError('Amount must be ≥ 0');
    setSubmitting(true);
    try {
      await postBooksBudget({
        term_id: Number(termId),
        expense_category_id: Number(categoryId),
        amount: amt,
        notes: notes.trim() || null,
      });
      onSuccess();
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Save failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
          <h3 className="font-bold text-slate-900">Set budget</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Term *</label>
            <select value={termId} onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Pick a term…</option>
              {terms.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Category *</label>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Pick a category…</option>
              {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Budget amount (₦) *</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Notes</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
          <button onClick={onClose}
            className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
          <button onClick={submit} disabled={submitting}
            className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
            {submitting ? 'Saving…' : 'Save budget'}
          </button>
        </div>
      </div>
    </div>
  );
}
